import PropTypes from 'prop-types';
import React from 'react';

import { getPaginationRange, PAGINATION_ELLIPSIS } from 'ravenjs/utils/pagination';

import { ChevronButton, PageButton, PageEllipsis, PageNavGroup } from './styles';

/*
 * Thin chevron glyphs sized to fit the 24x24 Figma nav buttons (24px button -
 * 2 x space-4 padding = 16px icon box). `currentColor` lets the button color
 * tokens drive the enabled/disabled states. Scoped here rather than editing the
 * shared `ravenjs/lib/SvgIcon` chevrons (which are circular arrows).
 */
const SINGLE_LEFT = 'M10 12L6 8L10 4';
const SINGLE_RIGHT = 'M6 4L10 8L6 12';
const DOUBLE_LEFT = ['M12 12L8 8L12 4', 'M8 12L4 8L8 4'];
const DOUBLE_RIGHT = ['M4 4L8 8L4 12', 'M8 4L12 8L8 12'];

const strokeProps = {
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: 'none',
};

const ChevronIcon = ({ paths }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
        {paths.map(d => (
            <path key={d} d={d} {...strokeProps} />
        ))}
    </svg>
);

ChevronIcon.propTypes = {
    paths: PropTypes.arrayOf(PropTypes.string).isRequired,
};

/**
 * Centered page-navigation zone for the redesigned (GN-37779) pagination
 * footer: first/last double chevrons, prev/next single chevrons and the
 * windowed page-number buttons with ellipsis markers.
 *
 * @param {Object}   props
 * @param {number}   props.currentPage The active page (1-based)
 * @param {number}   props.lastPage    The final page number
 * @param {Function} props.onPageChange Called with (event, page) on navigation
 * @return {JSX.Element}
 */
function PaginationBarPageNav({ currentPage, lastPage, onPageChange }) {
    const isFirstPage = currentPage <= 1;
    const isLastPage = currentPage >= lastPage;
    // Per the Figma, keep the "…" gap by shrinking the per-side block for
    // smaller page counts: 4+4 for 10+ pages, 3+3 for 8-9, 2+2 for 6-7.
    const boundaryCount = Math.min(4, Math.max(2, Math.floor((lastPage - 2) / 2)));
    const pageItems = getPaginationRange(currentPage, lastPage, boundaryCount);

    const goTo = page => event => onPageChange(event, page);

    return (
        <PageNavGroup>
            <ChevronButton
                type="button"
                aria-label="First page"
                disabled={isFirstPage}
                onClick={isFirstPage ? undefined : goTo(1)}
            >
                <ChevronIcon paths={DOUBLE_LEFT} />
            </ChevronButton>
            <ChevronButton
                type="button"
                aria-label="Previous page"
                disabled={isFirstPage}
                onClick={isFirstPage ? undefined : goTo(currentPage - 1)}
            >
                <ChevronIcon paths={[SINGLE_LEFT]} />
            </ChevronButton>
            {pageItems.map((item, index) =>
                item === PAGINATION_ELLIPSIS ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <PageEllipsis key={`ellipsis-${index}`} aria-hidden="true">
                        …
                    </PageEllipsis>
                ) : (
                    <PageButton
                        key={item}
                        type="button"
                        aria-label={`Page ${item}`}
                        aria-current={item === currentPage ? 'page' : undefined}
                        isActive={item === currentPage}
                        onClick={item === currentPage ? undefined : goTo(item)}
                    >
                        {item}
                    </PageButton>
                )
            )}
            <ChevronButton
                type="button"
                aria-label="Next page"
                disabled={isLastPage}
                onClick={isLastPage ? undefined : goTo(currentPage + 1)}
            >
                <ChevronIcon paths={[SINGLE_RIGHT]} />
            </ChevronButton>
            <ChevronButton
                type="button"
                aria-label="Last page"
                disabled={isLastPage}
                onClick={isLastPage ? undefined : goTo(lastPage)}
            >
                <ChevronIcon paths={DOUBLE_RIGHT} />
            </ChevronButton>
        </PageNavGroup>
    );
}

PaginationBarPageNav.propTypes = {
    currentPage: PropTypes.number,
    lastPage: PropTypes.number,
    onPageChange: PropTypes.func,
};

PaginationBarPageNav.defaultProps = {
    currentPage: 1,
    lastPage: 1,
    onPageChange: () => {},
};

export default PaginationBarPageNav;
